import React, { useState } from 'react';
import { Download, Sparkles, Loader2, AlertCircle, Clock, Shield } from 'lucide-react';
import type { ActiveSpaceInfo } from './types';

interface DecisionDashboardProps {
  projectName: string;
  videoUrl: string;
  isPromoted: boolean;
  /** Days remaining before the raw file link expires */
  daysRemaining: number;
  activeSpaceInfo: ActiveSpaceInfo | null;
  onDownload: () => void;
  onPromote: () => Promise<{ success: boolean; error?: string }>;
  /** Opens the subscription plans modal when the user needs to upgrade */
  onOpenPlans?: () => void;
  downloading: boolean;
  primaryColor?: string;
  secondaryColor?: string;
}

export function DecisionDashboard({
  projectName,
  videoUrl,
  isPromoted,
  daysRemaining,
  activeSpaceInfo,
  onDownload,
  onPromote,
  onOpenPlans,
  downloading,
  primaryColor = '#7c3aed',
  secondaryColor = '#ec4899',
}: DecisionDashboardProps) {
  const [promoting, setPromoting] = useState(false);
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [promoted, setPromoted] = useState(isPromoted);

  const gradientBg = { background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` };
  const isExpired = daysRemaining <= 0 && !promoted;

  const handlePromote = async () => {
    setPromoting(true);
    setPromotionError(null);
    try {
      const result = await onPromote();
      if (result.success) {
        setPromoted(true);
      } else if (result.error === 'SLOT_LIMIT_REACHED') {
        setPromotionError('No available Active Space slots. Upgrade your plan for more slots.');
        if (onOpenPlans) onOpenPlans();
      } else {
        setPromotionError(result.error || 'Failed to promote. Please try again.');
      }
    } catch {
      setPromotionError('An unexpected error occurred.');
    } finally {
      setPromoting(false);
    }
  };

  if (promoted) {
    return (
      <div className="bg-slate-900/60 border border-green-800/50 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-green-400">Active Space</h3>
            <p className="text-xs text-slate-400 mt-1">
              This video is permanently hosted with a fully branded viewer. Downloads and share links will never expire.
            </p>
            {activeSpaceInfo && (
              <p className="text-xs text-slate-500 mt-2">
                Slots used: {activeSpaceInfo.currentCount} / {activeSpaceInfo.limit}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Expiration warning */}
      {!isExpired && daysRemaining <= 7 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl">
          <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-300">
            {daysRemaining === 1
              ? 'This video expires tomorrow.'
              : `This video expires in ${daysRemaining} days.`}
            {' '}Promote to Active Space for permanent hosting.
          </p>
        </div>
      )}

      {isExpired && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-xs text-red-300">
            This video has expired. Download and share links are no longer available.
            Re-run the generation (using credits) to create a new version.
          </p>
        </div>
      )}

      {/* Action cards */}
      {!isExpired && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Download Raw */}
          <button
            onClick={onDownload}
            disabled={downloading}
            className="group text-left bg-slate-900/60 border border-slate-700 hover:border-slate-600 rounded-xl p-4 transition-all hover:bg-slate-800/60"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center group-hover:bg-slate-600/50 transition-colors">
                {downloading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Download className="w-4 h-4 text-white" />}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white">
                  {downloading ? 'Downloading...' : 'Download Raw File'}
                </h4>
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Link expires in {daysRemaining} days. No branding applied.
            </p>
          </button>

          {/* Add to Active Space */}
          <button
            onClick={handlePromote}
            disabled={promoting}
            className="group text-left border rounded-xl p-4 transition-all hover:shadow-lg"
            style={{
              borderColor: `${primaryColor}40`,
              background: `linear-gradient(135deg, ${primaryColor}08, ${secondaryColor}08)`,
            }}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={gradientBg}>
                {promoting ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Sparkles className="w-4 h-4 text-white" />}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white">
                  {promoting ? 'Promoting...' : 'Add to Active Space'}
                </h4>
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Permanent hosting. Fully branded viewer. Portal links.
            </p>
            {activeSpaceInfo && (
              <p className="text-[10px] text-slate-500 mt-1.5">
                {activeSpaceInfo.available
                  ? `${activeSpaceInfo.limit - activeSpaceInfo.currentCount} slots available`
                  : onOpenPlans
                    ? <span className="cursor-pointer text-purple-400 hover:text-purple-300" onClick={(e) => { e.stopPropagation(); onOpenPlans(); }}>No slots available — upgrade to add more</span>
                    : 'No slots available — upgrade to add more'}
              </p>
            )}
          </button>
        </div>
      )}

      {promotionError && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-xs text-red-300">{promotionError}</p>
        </div>
      )}
    </div>
  );
}
